import type { MappingReviewOutcome, RestoredMappingReference } from 'services/captable';
import type { ReviewStatus } from 'store/useReviewStore';
import { replaceMappingRows, restoredRowsOnCell, restoredRowsOnSecurity } from './clearEvidence';
import {
  type MappingFlagged,
  mappingRowStatus,
  pendingMappingProposals,
  pickMappingProposal,
} from './mappingReferenceStatus';
import { syncReferenceGreen } from './securityAiPatches';

export type MappingReviewAction = 'accept' | 'dismiss' | 'reopen';

const STATUS_BY_ACTION: Record<MappingReviewAction, 'accepted' | 'dismissed' | 'pending'> = {
  accept: 'accepted',
  dismiss: 'dismissed',
  reopen: 'pending',
};

export const mappingReviewStatusOf = (action: MappingReviewAction): 'accepted' | 'dismissed' | 'pending' =>
  STATUS_BY_ACTION[action];

/** A card whose thumbs settle its AI Excel mappings rather than a field or a citation. */
export const isMappingSettleItem = (
  item: { canSettle?: boolean; canSettleCitation?: boolean; canSettleMapping?: boolean }
): boolean => Boolean(item.canSettleMapping && !item.canSettle && !item.canSettleCitation);

// A mapping row as the cell holds it, or as the generated security type spells it.
type MappingRef = { id?: number | null } | string | null;

/** Stamp the settled status and decision time on the targeted rows; untouched lists keep their identity. */
export const applyMappingReviewToRefs = <T extends MappingRef>(
  refs: T[] | undefined,
  mappingIds: number[],
  action: MappingReviewAction,
  serverUpdatedAt: ReadonlyMap<number, string> = new Map()
): T[] | undefined => {
  if (!Array.isArray(refs)) {
    return refs;
  }
  const targets = new Set(mappingIds);
  const status = mappingReviewStatusOf(action);
  // The server's decision time when the response carries it, else the settle time here.
  const settledAt = new Date().toISOString();
  let changed = false;
  const next = refs.map(ref => {
    if (typeof ref !== 'object' || ref?.id == null || !targets.has(ref.id)) {
      return ref;
    }
    changed = true;
    return { ...ref, status, updated_at: serverUpdatedAt.get(ref.id) ?? settledAt };
  });
  return changed ? next : refs;
};

export type MappingRow = MappingFlagged & { id?: number | null };

/** One review call's effect: the settled rows' new status plus the rows it retired and restored. */
export type MappingReviewChange = {
  mappingIds: number[];
  action: MappingReviewAction;
  retiredIds: number[];
  restored: RestoredMappingReference[];
  /** The server's decision time per settled row, when the response reports it. */
  updatedAtById?: ReadonlyMap<number, string>;
};

type MappingCell<T extends MappingRow = MappingRow> = {
  columnId?: number | string | null;
  alias?: string;
  className?: string | null;
  /** Saved rows a manual edit took off the cell; a restore never puts them back on it. */
  retiredMappingIds?: number[];
  mappingReferences?: T[];
  dispatchEvent?: () => void;
};

/** The review card fields that name the proposal it rules on and which half of the card the thumbs settle. */
type MappingCard<T> = {
  id: string;
  canSettle?: boolean;
  canSettleCitation?: boolean;
  canSettleMapping?: boolean;
  primaryMappingReference?: T;
  mappingIds?: number[];
  pendingProposalCount?: number;
};

type ReviewCardStore<C> = { reviewItems: C[]; setReviewItems: (items: C[]) => void };

// Rewrite one store card in place.
const patchReviewCard = <T, C extends MappingCard<T>>(
  getReviewStore: () => ReviewCardStore<C>,
  itemId: string,
  fields: Partial<MappingCard<T>>
): void => {
  const { reviewItems, setReviewItems } = getReviewStore();
  setReviewItems(reviewItems.map(card => (card.id === itemId ? { ...card, ...fields } : card)));
};

// The card fields that point at the proposal it rules on.
const cardOnProposal = <T extends MappingRow>(proposal: T, refs: T[] | undefined) => ({
  primaryMappingReference: proposal,
  mappingIds: proposal.id != null ? [proposal.id] : [],
  pendingProposalCount: pendingMappingProposals(refs).length,
});

/**
 * Hand a card whose value or citation is decided to the Excel proposal still waiting on its cell:
 * the store card becomes an Excel card ruling on that proposal and the caller keeps it `pending`.
 * Undefined when nothing waits.
 */
export const holdCardOnPendingProposal = <T extends MappingRow, C extends MappingCard<T>>(
  itemId: string,
  refs: T[] | undefined,
  getReviewStore: () => ReviewCardStore<C>
): 'pending' | undefined => {
  const [proposal] = pendingMappingProposals(refs);
  if (!proposal) {
    return undefined;
  }
  patchReviewCard(getReviewStore, itemId, {
    canSettle: false,
    canSettleCitation: false,
    canSettleMapping: true,
    ...cardOnProposal(proposal, refs),
  });
  return 'pending';
};

type MappingSecurity = {
  id?: number;
  mapping_references?: MappingRef[];
};

// One rows list after a review change: settled rows restamped, retired rows dropped, restored rows merged in.
const rowsAfterReviewChange = <T extends MappingRef>(
  refs: T[] | undefined,
  change: MappingReviewChange,
  restored: T[]
): T[] | undefined => {
  const settled = applyMappingReviewToRefs(refs, change.mappingIds, change.action, change.updatedAtById);
  return replaceMappingRows(settled, change.retiredIds, restored);
};

/** Apply a review change to the cell object the spreadsheet holds, in place, then repaint it. */
export const applyMappingReviewToCell = (cell: MappingCell | null | undefined, change: MappingReviewChange) => {
  if (!cell || !Array.isArray(cell.mappingReferences)) {
    return;
  }
  // eslint-disable-next-line no-param-reassign
  cell.mappingReferences = rowsAfterReviewChange(
    cell.mappingReferences,
    change,
    restoredRowsOnCell(change.restored, cell)
  );
  // A repaint does not rerun the grid conditions, so the green follows the new active reference here.
  syncReferenceGreen(cell);
  cell.dispatchEvent?.();
};

/** Apply a review change to the store securities in place: a replaced store would rebuild the grid. */
export const applyMappingReviewToSecurities = (
  securities: MappingSecurity[] | undefined,
  change: MappingReviewChange
): void => {
  securities?.forEach(security => {
    const refs = rowsAfterReviewChange(
      security.mapping_references,
      change,
      restoredRowsOnSecurity(change.restored, security.id)
    );
    if (refs !== security.mapping_references) {
      // eslint-disable-next-line no-param-reassign
      security.mapping_references = refs;
    }
  });
};

type SettleMappingReviewParams<T extends MappingRow, C extends MappingCard<T>> = {
  itemId: string;
  mappingIds: number[];
  action: MappingReviewAction;
  persist: (mappingIds: number[], action: MappingReviewAction) => Promise<MappingReviewOutcome | undefined>;
  /** Read after the reply, so a grid rebuild during the request is patched on its new cell. */
  getCell: () => MappingCell<T> | null | undefined;
  getSecurities: () => MappingSecurity[] | undefined;
  getReviewStore: () => ReviewCardStore<C>;
};

/** Persist a settle of the card's Excel proposal, mirror it in place on the cell, the securities and the card, and resolve the card's next status. */
export const settleMappingReview = async <T extends MappingRow, C extends MappingCard<T>>({
  itemId,
  mappingIds,
  action,
  persist,
  getCell,
  getSecurities,
  getReviewStore,
}: SettleMappingReviewParams<T, C>): Promise<ReviewStatus> => {
  const response = await persist(mappingIds, action);
  const cell = getCell();
  // The server may also have retired the rows an accept replaced and restored the rows an undo brings back.
  const change: MappingReviewChange = {
    mappingIds,
    action,
    retiredIds: response?.retired_ids ?? [],
    restored: response?.restored ?? [],
    updatedAtById: new Map(
      (response?.results ?? []).flatMap(result => (result.updated_at ? [[result.id, result.updated_at] as const] : []))
    ),
  };
  applyMappingReviewToCell(cell, change);
  applyMappingReviewToSecurities(getSecurities(), change);
  const refs = cell?.mappingReferences;
  const next = pickMappingProposal(refs);
  if (!next) {
    return mappingReviewStatusOf(action);
  }
  // The card moves to the cell's next proposal now, so a click before the grid re-parses rules on it.
  patchReviewCard(getReviewStore, itemId, cardOnProposal(next, refs));
  return mappingRowStatus(next);
};
